// SCN Dispatch voice path — Project Rubidium (v3 protocol).
//
// Replaces the old MediaRecorder/decodeAudioData voice plumbing with IMBE
// frames carried over a Socket.IO `v3_packet` event. Multiple voice channels
// can be live at once; each gets its own VocoderDecoder. A single shared
// AudioContext is used so all decoder output sums into the same destination.
//
// Wire format (v3_packet):
//   { streamId, seq, ciphertext: Uint8Array(27), pcm?: Uint8Array(960),
//     subscriberID, alias }
//   - streamId: UUID v4, fresh per PTT-down
//   - seq: first-frame seq within the packet (increments by 3 per packet)
//   - ciphertext: 3 × 9-byte AES-256-CTR IMBE frames concatenated
//   - pcm: optional 8 kHz / 16-bit / mono PCM, 480 samples = 960 bytes
//
// Encryption: per-channel key supplied by webradio.js via setEncryptionKey()
// before the channel is joined. SHA-256(humanKey) -> 32 bytes, stored in
// `_keyMap` keyed by the full `vc[systemID:channelID]` string. Empty/null
// human keys map to 32 zero bytes (clear-call parity with SCNP25). Keys are
// never embedded in the channel name string or in any wire payload.

class VoiceV3 {
  constructor(socket) {
    // Shared graph nodes for legacy parity. compressor stays disconnected on
    // purpose — keeping the parity finding from the SCNP25 cutover.
    //
    // B1 fix (AudioContext proliferation → won't-open): the dispatch console
    // creates one VoiceV3 per monitored tile / incoming-sub call. Previously
    // each built its OWN AudioContext and _teardownDecoder never closed it, so
    // opening/closing tiles leaked contexts and, past the browser's per-page
    // hardware-context cap (~6), `new AudioContext` threw inside this ctor and
    // Channel.toggleMonitor swallowed it → the tile refused to open. Every
    // instance only ever summed into audioContext.destination anyway, so we now
    // share ONE page-wide AudioContext across all instances (each keeps its own
    // gainNode fader + decoders). N tiles → 1 context → no leak, no cap.
    this.audioContext = VoiceV3._getSharedAudioContext();
    this.filter1 = this.audioContext.createBiquadFilter();
    this.filter2 = this.audioContext.createBiquadFilter();
    this.compressor = this.audioContext.createDynamicsCompressor();
    this.gainNode = this.audioContext.createGain();

    // Voice state.
    this.cbVoiceMetadata = undefined;
    this.currentChannel = undefined;
    this.currentVolume = 1;
    this.gainNode.gain.value = this.currentVolume / 1.5;
    this.voiceSubID = undefined;
    this.alias = undefined;
    // Rev 8: TG this driver is currently TXing on (or 0/undefined when idle).
    // Stamped on every outbound v3_packet so downstream receivers can validate
    // the frame is for the TG they joined the vc for — closes the audio-leak
    // window when a vc is recycled mid-hangtime for a different TG. Source on
    // the dispatch side: channel_preempt_ok.talkgroup (preempt) or the
    // Channel.tgID at PTT-down (normal).
    this.txTalkgroup = 0;

    // Master output bus. The gainNode is the single fader for everything the
    // dispatch's voice path emits — all decoders route their output through it
    // (see _spinupDecoder). By default it feeds audioContext.destination (the
    // system default device). When setAudioDevice() is given a non-default
    // device, we lazily build a MediaStreamAudioDestinationNode + <audio>
    // element and use setSinkId() to route the stream to the chosen output.
    this._attachMasterOutput();

    // PTT / encoder state.
    this.socket = socket;
    this.pttDown = false;
    this.encoder = undefined;
    this._frameBuffer = [];          // up to 3 9-byte frames waiting on packetisation
    this._pcmBuffer = [];            // up to 3 Float32Array(160) for the same packet
    this._packetIndex = 0;           // packets sent during current PTT
    this._currentStreamId = undefined;
    this._exposePCMForCurrentTx = false;
    this._autogain = true;           // dispatch default; override via setAutogain

    // Multi-channel decoder map: channel name (vc[...]) -> VocoderDecoder.
    this.decoders = new Map();
    // Track active stream id per channel so we can route metadata callbacks.
    this._channelMeta = new Map();   // channel -> { subscriberID, alias }
    // Per-channel SHA-256 key bytes, keyed by the full `vc[systemID:channelID]`
    // string. Populated by setEncryptionKey() before the channel is joined so
    // decoder/encoder construction can read synchronously.
    this._keyMap = new Map();        // channel -> Uint8Array(32)
    // Rev 8: per-vc remembered TG. Set on setVoiceChannel(channel, joinedTg)
    // for both TX joins and RX joins. Inbound v3_packet, vc_stream_transition,
    // and vc_no_call are validated against this; any mismatch leaves the vc.
    // For preempt TX (joining preemptVc but bridging to the TG vc), the
    // caller passes the underlying TG so server-bridged frames carrying that
    // TG validate cleanly.
    this._joinedTgByChannel = new Map(); // channel -> tgID (number)
    // Rev 8: per-vc current-stream record, populated by vc_stream_transition.
    // Used for idempotency (skip a re-emit if we already know the newStreamId)
    // and for late-join recovery (server may re-emit on vc_join).
    this._activeStreamByChannel = new Map(); // channel -> newStreamId
    // Rev 8: per-vc hangtime watchdog. Server emits vc_no_call at ~100ms
    // cadence during the post-call hangtime window. Three consecutive
    // misses (~300ms) and we leave the vc — matches §6.4 / §7.2.
    this._hangtimeWatchdogByChannel = new Map(); // channel -> { timer, missCount }
    // B2 fix (cross-channel contamination): timestamp of the last inbound
    // v3_packet actually decoded on each channel. The payload-only vc-events
    // (vc_stream_transition / vc_no_call) carry NO room/channel — only a
    // targetTalkgroup — and are delivered to EVERY VoiceV3 sharing the socket.
    // A sibling instance that merely shares a joinedTg (e.g. a preempt vc over
    // the same TG) must NOT be perturbed by an event for a room it isn't
    // actually in a call on. We only apply those events to a channel that is
    // demonstrably engaged in the referenced call — recent RX or an already-
    // armed hangtime watchdog (see _channelEngagedInCall).
    this._lastRxAtByChannel = new Map(); // channel -> epoch ms of last decoded frame
    this.rxAssociationWindowMs = 3000;   // > max hangtime (1s / 2.5s post-preempt)
    this.hangtimeMissTolerance = 3;
    this.hangtimeMissIntervalMs = 100;   // canonical server cadence (Q1 resolution)
    // Rev 8 capability handshake (PL-1/PL-2): set by setVoiceChannel from the
    // ConsoleSession's serverRev8 flag. When false, _verdictForIncomingTg
    // treats unstamped frames as legal (pre-Rev-8 server behavior — server
    // doesn't stamp targetTalkgroup at all). When true, strict Rev-8
    // semantics apply (unstamped → drop, mismatched → leave).
    this.serverRev8 = false;

    console.info("[VOICE] Init voice (Rubidium v3)");

    this.socket.on("v3_packet", (channel, pkt) => {
      this._handleV3Packet(channel, pkt);
    });
    // Rev 8 — vc-only signaling primitive: emitted by the server when the
    // talker on a vc swaps mid-stream (preempt onset or preempt-end-restore).
    // Server emit signature is (payload) only — the room is the addressing
    // mechanism, payload carries targetTalkgroup for receiver validation.
    // Idempotent decoder reset; no client-side state mutation beyond the
    // decoder. Receivers in the vc see it; no one else does.
    this.socket.on("vc_stream_transition", (packet) => {
      this._handleVcStreamTransition(packet);
    });
    // Rev 8 — periodic vc-only "no-call" heartbeat. Server emits (payload)
    // only at ~100ms cadence during the post-call hangtime window so radios
    // in the new stay-joined state can tell "channel is silent because
    // hangtime" apart from "channel is gone." Three consecutive misses
    // (~300ms) on the watchdog => leave the vc.
    this.socket.on("vc_no_call", (packet) => {
      this._handleVcNoCall(packet);
    });
  }

  // B1 fix: one page-wide AudioContext shared by every VoiceV3 instance. Lazily
  // created; recreated only if a previous one was explicitly closed. Never
  // closed on per-tile teardown, so opening/closing tiles no longer creates or
  // leaks contexts (the root of the "channel won't open" cap-exhaustion).
  static _getSharedAudioContext() {
    const existing = VoiceV3._sharedAudioContext;
    if (existing && existing.state !== "closed") return existing;
    const ctx = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: 48000,
      latencyHint: "interactive"
    });
    VoiceV3._sharedAudioContext = ctx;
    return ctx;
  }

  // Build the master output bus. Default routing is gainNode -> destination.
  // setAudioDevice() will rewire the master gain node to a stream destination
  // when a non-default sink is chosen.
  _attachMasterOutput() {
    this.gainNode.connect(this.audioContext.destination);
    this._currentSink = "default"; // 'default' | 'custom'
    // Lazily-created machinery for non-default sinks.
    this._streamDest = null;
    this._sinkAudio = null;
  }

  // Resume the AudioContext if it was created before any user gesture. Safe
  // to call repeatedly. Returns a promise that resolves once the context is
  // running (or fails silently if resume rejects).
  _ensureAudioContextRunning() {
    if (this.audioContext.state === "suspended") {
      return this.audioContext.resume().catch(() => {});
    }
    return Promise.resolve();
  }

  // Set the master output volume. Drives gainNode.gain directly so the change
  // is live across all currently-decoding channels (no per-decoder snapshot).
  setVolume(value) {
    const v = Number(value);
    if (!Number.isFinite(v)) return;
    this.currentVolume = v;
    this.gainNode.gain.value = v / 1.5;
  }

  async setAudioDevice(device) {
    console.debug("Set audio device", device);
    if (!device || device === null || !device.deviceId || device.deviceId === "default") {
      // Switch back to the system default output. Tear down stream routing so
      // gainNode feeds audioContext.destination directly.
      try { this.gainNode.disconnect(); } catch (e) {}
      this.gainNode.connect(this.audioContext.destination);
      if (this._sinkAudio) {
        try { this._sinkAudio.pause(); } catch (e) {}
      }
      this._currentSink = "default";
      return;
    }
    // Custom sink: gainNode -> MediaStreamDestination -> <audio> with setSinkId.
    if (!this._streamDest) {
      this._streamDest = this.audioContext.createMediaStreamDestination();
    }
    if (!this._sinkAudio) {
      this._sinkAudio = new Audio();
      this._sinkAudio.autoplay = true;
    }
    try { this.gainNode.disconnect(); } catch (e) {}
    this.gainNode.connect(this._streamDest);
    this._sinkAudio.srcObject = this._streamDest.stream;
    try { await this._sinkAudio.setSinkId(device.deviceId); } catch (e) {
      console.warn("[VOICE] setSinkId failed", e);
    }
    try { await this._sinkAudio.play(); } catch (e) {
      console.warn("[VOICE] sink audio play failed (gesture required?)", e);
    }
    this._currentSink = "custom";
  }

  setSubscriber(subscriberID) {
    this.voiceSubID = subscriberID;
  }

  setAlias(alias) {
    this.alias = alias;
  }

  // Rev 8: caller sets the TG this driver will TX on. Stamped on every
  // outbound v3_packet via _emitPacket. Cleared (or re-set) by the caller
  // around setPTT toggles. The preempt path passes channel_preempt_ok
  // .talkgroup; the normal path passes Channel.tgID.
  setTalkgroup(tg) {
    const n = typeof tg === "number" ? tg : parseInt(tg, 10);
    this.txTalkgroup = Number.isFinite(n) ? n : 0;
  }

  setAutogain(value) {
    this._autogain = !!value;
  }

  // Allow webradio to opt the next transmission into PCM exposure when the
  // CHANNEL_GRANT advertised accessibility listeners on the talkgroup.
  setAccessibilityListenersPresent(value) {
    this._exposePCMForCurrentTx = !!value;
  }

  // --- per-channel key registration ---------------------------------------
  // Called by webradio.js with the channel string in its new keyless form
  // `vc[systemID:channelID]` (or the raw `systemID:channelID` inner — both are
  // accepted) and the human-readable per-channel key. SHA-256 derives 32 bytes
  // which are cached in `_keyMap` so spin-up/encoder construction can read
  // synchronously. Empty/null key => 32 zero bytes (parity with SCNP25).
  async setEncryptionKey(channelString, keyString) {
    if (typeof channelString !== "string" || channelString.length === 0) return;
    const channel = channelString.startsWith("vc[")
      ? channelString
      : "vc[" + channelString + "]";
    // Store the Promise immediately so any caller (decoder spinup, encoder
    // start) that fires before SHA-256 resolves can await it instead of
    // racing past with the all-zeros fallback. Once resolved, swap in the
    // bytes so subsequent lookups skip the Promise machinery.
    const promise = VoiceV3._deriveKeyBytes(keyString);
    this._keyMap.set(channel, promise);
    const keyBytes = await promise;
    if (this._keyMap.get(channel) === promise) {
      this._keyMap.set(channel, keyBytes);
    }
  }

  static async _deriveKeyBytes(humanKey) {
    if (humanKey === null || humanKey === undefined || humanKey === "" || humanKey === false) {
      // No key: clear-call, all-zero key (parity with SCNP25).
      return new Uint8Array(32);
    }
    const buf = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(String(humanKey))
    );
    return new Uint8Array(buf);
  }

  async _keyForChannel(channelString) {
    const k = this._keyMap.get(channelString);
    if (k instanceof Promise) return await k;
    if (k) return k;
    // Channel was joined without a prior setEncryptionKey() call. Fall back to
    // a clear-call zero key so we don't crash; webradio.js should always set
    // the key first, but a missing call shouldn't take voice down.
    return new Uint8Array(32);
  }

  // --- voice channel join / leave -----------------------------------------
  // Rev 8: joinedTg is the TG to validate inbound frames/heartbeats against.
  // Callers should pass it on every join (TX and RX); omitted == "don't
  // validate" (mixed-version compat: dispatch layers that don't yet pass
  // joinedTg fall back to today's heartbeat-only TG semantics).
  //
  // serverRev8 is the capability-handshake flag (PL-1) — true when the
  // signaling server echoed "v1.1" on console_activate_ok, false against
  // pre-Rev-8 servers that don't stamp targetTalkgroup. Defaults to the
  // current instance value when omitted so legacy callers (tests, future
  // listener-only paths) don't accidentally clobber it.
  setVoiceChannel(newChannel, joinedTg, serverRev8) {
    if (serverRev8 !== undefined) {
      this.serverRev8 = !!serverRev8;
    }
    if (this.currentChannel !== undefined) {
      this._teardownDecoder(this.currentChannel);
      this.socket.emit("vc_leave", this.currentChannel);
      this._joinedTgByChannel.delete(this.currentChannel);
      this.currentChannel = undefined;
    }
    if (newChannel != undefined) {
      this.pttDown = false;
      this.currentChannel = "vc[" + newChannel + "]";
      this.socket.emit("vc_join", this.currentChannel);
      if (joinedTg !== undefined && joinedTg !== null) {
        const tg = typeof joinedTg === "number" ? joinedTg : parseInt(joinedTg, 10);
        if (Number.isFinite(tg) && tg > 0) {
          this._joinedTgByChannel.set(this.currentChannel, tg);
        }
      }
      this._ensureAudioContextRunning();
      this._spinupDecoder(this.currentChannel).catch(e =>
        console.error("[VOICE] decoder spinup failed", e)
      );
    }
  }

  leaveVoiceChannel() {
    this.setVoiceChannel(null);
  }

  // Rev 8: shared TG verdict for inbound v3 signaling (v3_packet,
  // vc_stream_transition, vc_no_call). Tristate per Steven's cross-validation
  // 2026-05-16:
  //   "ok"    — field present, matches joinedTg → process normally
  //   "leave" — field present, mismatches joinedTg → vc_leave + log
  //   "drop"  — field MISSING on a v3 packet → protocol violation
  //             (server defense-in-depth stamps targetTalkgroup on all v3
  //             relays, incl. A7-talker pass-through, so receivers should
  //             never see a v3 frame without it). Caller drops the
  //             frame/event + logs warn. NOT a vc-leave trigger.
  // If we never recorded a joinedTg for this vc (caller didn't pass one on
  // setVoiceChannel), return "ok" — can't validate, trust the frame. v2 voice
  // is unaffected (no Rev 8 wire surface applies to v2).
  //
  // Rev 8 capability handshake (PL-2): pre-Rev-8 signaling servers never
  // stamp targetTalkgroup. Against those (serverRev8 === false), an
  // unstamped frame is normal-and-legal, not a protocol violation — treat
  // as "ok". Mismatch handling stays strict either way: if the server *did*
  // stamp something and it disagrees with joinedTg, leave the vc.
  _verdictForIncomingTg(channel, incomingTg) {
    const expected = this._joinedTgByChannel.get(channel);
    if (expected === undefined) {
      return "ok";
    }
    if (incomingTg === undefined || incomingTg === null || incomingTg === 0) {
      return this.serverRev8 ? "drop" : "ok";
    }
    const got = typeof incomingTg === "number" ? incomingTg : parseInt(incomingTg, 10);
    if (!Number.isFinite(got)) {
      return this.serverRev8 ? "drop" : "ok";
    }
    return got === expected ? "ok" : "leave";
  }

  // Rev 8: invoked from any of the three validation paths when a TG
  // mismatch fires. Tears down the per-channel decoder and emits vc_leave.
  // Distinct from leaveVoiceChannel() because the mismatched vc may not be
  // this.currentChannel (future multi-channel listener path); we want to
  // leave the specific vc that failed, not whichever one is "current."
  _leaveOnTgMismatch(channel, reason) {
    console.warn("[VOICE] leaving vc " + channel + " — " + reason);
    if (this.currentChannel === channel) {
      this.leaveVoiceChannel();
    } else {
      this._teardownDecoder(channel);
      try { this.socket.emit("vc_leave", channel); } catch (_) {}
    }
  }

  async _spinupDecoder(channelString) {
    if (this.decoders.has(channelString)) return;
    const keyBytes = await this._keyForChannel(channelString);
    await Rubidium.ready();
    const decoder = new Rubidium.VocoderDecoder({
      audioContext: this.audioContext,
      encryption: { key: keyBytes },
      playbackLeadSec: 0.08,
      outputGain: 1.0,                 // master fader handles user volume now
      outputNode: this.gainNode        // route through the master bus
    });
    decoder.onError(err => {
      // Suppress underrun resyncs; they're informational.
      if (err && /underrun/.test(err.message || "")) return;
      console.warn("[VOICE] decoder error", err);
    });
    this.decoders.set(channelString, decoder);
  }

  _teardownDecoder(channelString) {
    const dec = this.decoders.get(channelString);
    if (dec) {
      try { dec.stop(); } catch (e) {}
      this.decoders.delete(channelString);
    }
    this._channelMeta.delete(channelString);
    // Drop the cached key for this channel; webradio.js will re-set it on
    // the next join. Stale keys are harmless but no point keeping them.
    this._keyMap.delete(channelString);
    // Rev 8: clear the current-stream record so a future re-join on the
    // same vc starts fresh (and any in-flight vc_stream_transition for the
    // prior stream is naturally treated as a stale no-op).
    this._activeStreamByChannel.delete(channelString);
    // Rev 8: cancel the per-vc hangtime watchdog. Future joins re-arm via
    // _handleVcNoCall on the next inbound heartbeat.
    this._clearHangtimeWatchdog(channelString);
    // Rev 8: forget the joinedTg; next setVoiceChannel call will re-establish.
    this._joinedTgByChannel.delete(channelString);
    // B2 fix: drop the engagement timestamp for this channel.
    this._lastRxAtByChannel.delete(channelString);
  }

  // B2 fix: is this channel demonstrably engaged in the call a payload-only
  // vc-event refers to? True when it recently decoded a frame (within the
  // hangtime-association window) or already has an armed hangtime watchdog.
  // Guards against a sibling instance that only shares the event's
  // targetTalkgroup (e.g. a preempt vc over the same TG) being mutated by an
  // event meant for a different room.
  _channelEngagedInCall(channel) {
    if (this._hangtimeWatchdogByChannel.has(channel)) return true;
    if (this._activeStreamByChannel.has(channel)) return true;
    const last = this._lastRxAtByChannel.get(channel);
    return last !== undefined && (Date.now() - last) <= this.rxAssociationWindowMs;
  }

  // --- inbound v3 packet --------------------------------------------------
  _handleV3Packet(channel, pkt) {
    try {
      // Defensive: first inbound packet after a join may arrive before any
      // user gesture. Resume context (no-op if already running).
      this._ensureAudioContextRunning();
      const decoder = this.decoders.get(channel);
      if (!decoder) {
        // Race / stale packet for a channel we're no longer on.
        return;
      }
      // Rev 8 §3.11: per-frame TG validation. Verdict tristate per Steven's
      // 2026-05-16 tightening — present-and-mismatched leaves the vc;
      // present-and-missing is a protocol violation (server stamps v3
      // relays defensively for A7 talkers), drop frame + warn but stay
      // joined; matching is the normal path.
      if (pkt) {
        const v = this._verdictForIncomingTg(channel, pkt.targetTalkgroup);
        if (v === "leave") {
          this._leaveOnTgMismatch(channel, "v3_packet targetTalkgroup=" + pkt.targetTalkgroup + " ≠ joinedTg=" + this._joinedTgByChannel.get(channel));
          return;
        }
        if (v === "drop") {
          console.warn("[VOICE] v3_packet on " + channel + " missing targetTalkgroup — dropping frame (protocol violation)");
          return;
        }
      }
      // Surface metadata to the rest of dispatch (subscriber id, alias).
      if (this.cbVoiceMetadata && pkt && (pkt.subscriberID !== undefined || pkt.alias !== undefined)) {
        try {
          this.cbVoiceMetadata({
            subscriberID: pkt.subscriberID,
            alias: pkt.alias,
            enhancedVoice: true
          });
        } catch (_) {}
      }
      const ct = pkt.ciphertext instanceof Uint8Array
        ? pkt.ciphertext
        : new Uint8Array(pkt.ciphertext);
      if (ct.length !== 27) {
        console.warn("[VOICE] v3_packet ciphertext wrong size:", ct.length);
        return;
      }
      for (let i = 0; i < 3; i++) {
        const slice = ct.subarray(i * 9, i * 9 + 9);
        decoder.feed(slice, { streamId: pkt.streamId, seq: pkt.seq + i });
      }
      // B2 fix: mark this channel as actively engaged in a call. Used by the
      // payload-only vc-event handlers to demux by real engagement, not just a
      // (possibly shared) joinedTg. v3_packet itself is channel-addressed, so
      // it needs no such gating.
      this._lastRxAtByChannel.set(channel, Date.now());
      // Rev 8: any inbound frame on a vc cancels the hangtime watchdog —
      // server stops emitting vc_no_call as soon as a real call resumes
      // (§3.9), and frames themselves are the affirmative signal of
      // "channel is live." Watchdog re-arms when the next hangtime begins.
      this._clearHangtimeWatchdog(channel);
    } catch (e) {
      console.error("[VOICE] error handling v3 packet", e);
    }
  }

  // Rev 8 — vc_stream_transition: server-emitted hint that the talker on a
  // vc just swapped (preempt onset, preempt-end-restore, etc.). Tells the
  // decoder to finalize the ending stream and prepare for the new one
  // *between* streams (rather than inferring the swap one frame late off a
  // streamId change). Carries the new talker's RID for caller metadata.
  //
  // joinedTg / targetTalkgroup validation is added in a later commit; for
  // now we only act on the decoder. Idempotent: re-emit with the same
  // newStreamId is a no-op.
  _handleVcStreamTransition(packet) {
    try {
      if (!packet || typeof packet !== "object") return;
      // Rev 8 (signaling-audit correction 2026-05-16): vc_stream_transition
      // is emitted with (payload) only — the room is the addressing
      // mechanism. The handler's vc-context is our currentChannel; if we
      // aren't joined to one, the event is for someone else's room.
      const channel = this.currentChannel;
      if (!channel) return;
      const decoder = this.decoders.get(channel);
      if (!decoder) {
        // Not decoding on our currentChannel (race during teardown). Drop.
        return;
      }
      // Rev 8 §3.8 / §6.4 + Steven 2026-05-16 tightening: validate the
      // transition's targetTalkgroup against the TG we joined this vc for.
      //   "drop"  — field absent  → protocol violation (server stamps v3
      //             relays defensively); warn + return.
      //   "leave" — field present, mismatches joinedTg → per spec this is
      //             "vc recycled for unrelated TG, leave." But shared
      //             globalSocket means every VoiceV3 instance receives this
      //             event regardless of which room it was emitted to, so a
      //             mismatch here is almost certainly fanout from another
      //             Voice instance's room — leaving on it would false-leave
      //             a healthy vc. Recycle-detect is delegated to v3_packet
      //             validation (which carries channel arg + targetTalkgroup,
      //             no fanout ambiguity); we silent-ignore here.
      //   "ok"    — process.
      {
        const v = this._verdictForIncomingTg(channel, packet.targetTalkgroup);
        if (v === "drop") {
          console.warn("[VOICE] vc_stream_transition on " + channel + " missing targetTalkgroup — dropping event (protocol violation)");
          return;
        }
        if (v !== "ok") {
          // Mismatch — silent-ignore (shared-socket fanout safety).
          return;
        }
      }
      // B2 fix — close the shared-socket fanout holes:
      //  (1) no joinedTg recorded → we cannot attribute this payload-only event
      //      to our room, so fail CLOSED (was: verdict "ok" fell through and
      //      mutated our decoder/talker off another room's event).
      //  (2) shared joinedTg (e.g. a preempt vc over the same TG monitored on
      //      another tile) → targetTalkgroup alone can't disambiguate, so only
      //      apply the swap if THIS channel is actually engaged in the call.
      if (this._joinedTgByChannel.get(channel) === undefined) return;
      if (!this._channelEngagedInCall(channel)) return;
      const newStreamId = packet.newStreamId;
      if (typeof newStreamId !== "string" || newStreamId.length === 0) {
        return;
      }
      if (this._activeStreamByChannel.get(channel) === newStreamId) {
        // Idempotent — already prepped for this stream.
        return;
      }
      this._activeStreamByChannel.set(channel, newStreamId);
      // Surface the talker swap to dispatch metadata (alias absent on the
      // transition packet; alias comes back on the first v3_packet of the
      // new stream). subscriberID is the swap target — drives the per-tile
      // mid-call talker display.
      if (this.cbVoiceMetadata && packet.newRid !== undefined) {
        try {
          this.cbVoiceMetadata({
            subscriberID: packet.newRid,
            // Empty alias here — the receiver's handleVoiceMetadata treats
            // empty as "no alias on this frame", which is the right fallback
            // until the new stream's first frame lands carrying its alias.
            alias: "",
            enhancedVoice: true,
            streamTransition: true
          });
        } catch (_) {}
      }
      // Decoder reset: best-effort interface check — old Rubidium builds
      // may not expose finalize/prepareForStream, in which case we rely on
      // the per-frame streamId change in feed() to reset state implicitly
      // (one frame of stale buffer, acceptable per §7.10).
      if (typeof decoder.finalize === "function" && packet.endingStreamId) {
        try { decoder.finalize(packet.endingStreamId); } catch (_) {}
      }
      if (typeof decoder.prepareForStream === "function") {
        try { decoder.prepareForStream(newStreamId, packet.newRid); } catch (_) {}
      }
    } catch (e) {
      console.error("[VOICE] error handling vc_stream_transition", e);
    }
  }

  // Rev 8 — vc_no_call: per-vc hangtime heartbeat. Each tick resets the
  // missed-heartbeat counter; consecutive misses past tolerance leave the
  // vc. Server hangtime window is 1s normal / 2.5s post-preempt
  // (Q1 resolution); the radio's leave-trigger is purely heartbeat-driven
  // so the radio doesn't need to know which window applies.
  //
  // targetTalkgroup validation against the per-vc joinedTg is wired in a
  // later commit (joinedTg lifecycle); for now we only run the watchdog.
  _handleVcNoCall(packet) {
    try {
      // Rev 8 (signaling-audit correction 2026-05-16): vc_no_call is emitted
      // with (payload) only. Our vc-context is currentChannel; if we aren't
      // joined to one, the event is for someone else's room.
      const channel = this.currentChannel;
      if (!channel || !this.decoders.has(channel)) return;
      // Rev 8 §3.9 / §6.4 + Steven 2026-05-16 tightening + signaling-audit
      // correction: validate targetTalkgroup against our joinedTg.
      //   "drop"  — field absent → protocol violation, warn + return
      //             (watchdog ticks down naturally on the missed heartbeat).
      //   "leave" — mismatch → per spec means "vc recycled mid-hangtime."
      //             But shared globalSocket fanout means a mismatch here is
      //             almost always another Voice instance's room — silent-
      //             ignore to avoid false-leaves on healthy vcs. Recycle-
      //             detect is delegated to v3_packet validation (channel
      //             arg + per-frame targetTalkgroup, no fanout ambiguity).
      //   "ok"    — reset the watchdog.
      if (packet) {
        const v = this._verdictForIncomingTg(channel, packet.targetTalkgroup);
        if (v === "drop") {
          console.warn("[VOICE] vc_no_call on " + channel + " missing targetTalkgroup — dropping heartbeat (protocol violation)");
          return;
        }
        if (v !== "ok") {
          // Mismatch — silent-ignore (shared-socket fanout safety).
          return;
        }
      }
      // B2 fix — same fanout-demux guards as vc_stream_transition. Without
      // these, a sibling tile that merely shares this heartbeat's
      // targetTalkgroup (or has no joinedTg at all) would keep resetting its
      // hangtime watchdog off another room's heartbeat and never time out —
      // the "stuck channel" symptom. Only a channel actually engaged in the
      // referenced call (recent RX or an armed watchdog) reacts.
      if (this._joinedTgByChannel.get(channel) === undefined) return;
      if (!this._channelEngagedInCall(channel)) return;
      this._resetHangtimeWatchdog(channel);
    } catch (e) {
      console.error("[VOICE] error handling vc_no_call", e);
    }
  }

  // (Re)start the per-vc hangtime watchdog. Each call resets the miss
  // counter and re-arms the timer; tolerance × intervalMs without a tick
  // triggers _onHangtimeExpired which leaves the vc.
  _resetHangtimeWatchdog(channel) {
    const existing = this._hangtimeWatchdogByChannel.get(channel);
    if (existing && existing.timer) {
      try { clearTimeout(existing.timer); } catch (_) {}
    }
    const onExpire = () => this._onHangtimeExpired(channel);
    const timer = setTimeout(onExpire, this.hangtimeMissIntervalMs * this.hangtimeMissTolerance);
    this._hangtimeWatchdogByChannel.set(channel, { timer, missCount: 0 });
  }

  _clearHangtimeWatchdog(channel) {
    const existing = this._hangtimeWatchdogByChannel.get(channel);
    if (existing && existing.timer) {
      try { clearTimeout(existing.timer); } catch (_) {}
    }
    this._hangtimeWatchdogByChannel.delete(channel);
  }

  // Three consecutive heartbeats missed (~300ms) on a vc that's in the
  // stay-joined hangtime window → leave the vc. Recovery is via the normal
  // CHANNEL_REQ → CHANNEL_GRANT path on the next call.
  _onHangtimeExpired(channel) {
    if (!this.decoders.has(channel)) {
      // Already left this vc.
      this._clearHangtimeWatchdog(channel);
      return;
    }
    console.info("[VOICE] hangtime watchdog expired on " + channel + " — leaving vc");
    // If this is our current join, drop it via the normal leave path so
    // setVoiceChannel(null) state stays consistent.
    if (this.currentChannel === channel) {
      this.leaveVoiceChannel();
    } else {
      // We're decoding on a vc we never explicitly joined as "current" —
      // (e.g. a future multi-channel listener path). Tear it down directly.
      this._teardownDecoder(channel);
      this.socket.emit("vc_leave", channel);
    }
    this._clearHangtimeWatchdog(channel);
  }

  // --- PTT / outbound -----------------------------------------------------
  async setPTT(pttValue) {
    this.pttDown = pttValue;
    if (pttValue) {
      // PTT-down is an immediate user gesture — guaranteed permitted moment
      // to resume a suspended AudioContext.
      await this._ensureAudioContextRunning();
      await this._startEncoder();
    } else {
      this._stopEncoder();
    }
  }

  async _startEncoder() {
    if (this.encoder || this.currentChannel === undefined) return;
    const keyBytes = await this._keyForChannel(this.currentChannel);
    await Rubidium.ready();

    this._frameBuffer = [];
    this._pcmBuffer = [];
    this._packetIndex = 0;
    this._currentStreamId = undefined;

    const exposePCM = this._exposePCMForCurrentTx === true;

    this.encoder = new Rubidium.VocoderEncoder({
      source: "mic",
      audioConstraints: {
        autoGainControl: this._autogain,
        googAutoGainControl: this._autogain,
        echoCancellation: false,
        noiseSuppression: false,
        channelCount: 1
      },
      encryption: { key: keyBytes },
      exposePCM: exposePCM
    });

    const channelAtStart = this.currentChannel;

    this.encoder.onError(err => console.error("[VOICE] encoder error", err));
    this.encoder.onFrame((frame, meta) => {
      if (!this.pttDown) return;
      if (this._currentStreamId === undefined) {
        this._currentStreamId = meta.streamId;
      }
      this._frameBuffer.push(frame);
      if (exposePCM && meta.pcm) this._pcmBuffer.push(meta.pcm);
      if (this._frameBuffer.length === 3) {
        this._emitPacket(channelAtStart);
      }
    });

    try {
      await this.encoder.start();
    } catch (e) {
      console.error("[VOICE] encoder start failed", e);
      this.encoder = undefined;
    }
  }

  _emitPacket(channel) {
    if (channel === undefined || this.currentChannel !== channel) {
      // Channel switched mid-flight; drop.
      this._frameBuffer = [];
      this._pcmBuffer = [];
      return;
    }
    const ciphertext = new Uint8Array(27);
    for (let i = 0; i < 3; i++) ciphertext.set(this._frameBuffer[i], i * 9);
    const seq = this._packetIndex * 3;
    const pkt = {
      streamId: this._currentStreamId,
      seq: seq,
      ciphertext: ciphertext,
      subscriberID: this.voiceSubID,
      alias: this.alias,
      // Rev 8 per §3.11: stamp the TG this frame is for. Receivers validate
      // pkt.targetTalkgroup === joinedTg every frame; mismatch leaves the vc
      // immediately (closes the vc-recycle-mid-hangtime audio-leak window).
      // Missing/zero on the receiver side falls back to heartbeat-driven
      // validation (Q3-followup default).
      targetTalkgroup: this.txTalkgroup
    };
    if (this._pcmBuffer.length === 3) {
      // Concatenate three Float32Array(160) -> 480 samples, convert to
      // 16-bit signed little-endian Uint8Array(960).
      const out = new Uint8Array(960);
      const view = new DataView(out.buffer);
      let o = 0;
      for (let s = 0; s < 3; s++) {
        const block = this._pcmBuffer[s];
        for (let n = 0; n < 160; n++) {
          let v = block[n];
          if (v > 1) v = 1; else if (v < -1) v = -1;
          const i16 = v < 0 ? Math.round(v * 0x8000) : Math.round(v * 0x7FFF);
          view.setInt16(o, i16, true);
          o += 2;
        }
      }
      pkt.pcm = out;
    }
    this.socket.emit("v3_packet", channel, pkt);
    this._packetIndex++;
    this._frameBuffer = [];
    this._pcmBuffer = [];
  }

  _stopEncoder() {
    if (this.encoder) {
      try { this.encoder.stop(); } catch (e) {}
      this.encoder = undefined;
    }
    this._frameBuffer = [];
    this._pcmBuffer = [];
    this._packetIndex = 0;
    this._currentStreamId = undefined;
    // Do NOT clear _exposePCMForCurrentTx — webradio sets it per CHANNEL_GRANT.
  }
}

// Expose as global for the voice.js router
if (typeof window !== "undefined") { window.VoiceV3 = VoiceV3; }
// CommonJS export for the Node-based unit tests (tests/). No-op in the browser.
if (typeof module !== "undefined" && module.exports) { module.exports = { VoiceV3 }; }

